/**
 * Núcleo HTTP — o ÚNICO lugar que fala `fetch` e o único que sabe como o
 * backend descreve erro.
 *
 * O contrato de erro do web_api é `{message, errors[], request_id}` —
 * nunca `detail` (há teste no back asserindo a AUSÊNCIA de `detail`,
 * `test_error_body_uses_message_and_errors_not_detail`). O parser aceita
 * `detail` só como fallback de transição, porque parte dos métodos do
 * `api-client.ts` ainda fala o contrato do backend descartado.
 *
 * `request_id` é o elo com o log do servidor: o corpo de todo erro traz
 * o id que o suporte consegue procurar no CloudWatch. Por isso ele viaja
 * no `ApiError` e entra na mensagem exibível — um usuário que cola o
 * código no chamado economiza a triagem inteira.
 *
 * Antes disso existir, o parsing de erro estava DUPLICADO três vezes
 * dentro do `api-client.ts` (request, stream do Copiloto e transcrição),
 * cada cópia lendo só `detail` — um 422 de validação renderizava
 * `[object Object]` pro usuário.
 */

import { oidcEnabled } from "@/shared/auth/oidc";
import { currentAccessToken, renew } from "@/shared/auth/session";

export type FieldError = { field?: string; message: string };

type WireError = {
  message?: string;
  errors?: Array<string | FieldError>;
  request_id?: string;
  /** Identificador ESTRUTURADO de uns poucos erros que a tela precisa
   * distinguir sem ler texto: `company_disabled`, `no_access` e `invalid_search`. */
  code?: string;
  detail?: unknown;
};

/** O 403 de quem abre uma empresa que um Máster desativou. */
export const COMPANY_DISABLED_CODE = "company_disabled";

/** O 403 de quem se autentica mas não tem acesso ao ReputAItion: conta
 *  inativada (perde o cargo no Keycloak), sem cadastro ou sem cargo
 *  (`AccessRevokedError` no servidor, 2026-10-15). */
export const NO_ACCESS_CODE = "no_access";

/** O 422 de uma expressão de busca de menções que não parseia. */
export const INVALID_SEARCH_CODE = "invalid_search";

/** O 422 de um pedido à IA que passou do que o modelo aceita de uma vez
 *  (o texto vai sem teto de caracteres no Versa, 2026-10-29). */
export const PROMPT_TOO_LARGE_CODE = "prompt_too_large";

/** Códigos em que não há defeito a procurar no log: sem código de suporte. */
const WITHOUT_SUPPORT_CODE = new Set([NO_ACCESS_CODE, PROMPT_TOO_LARGE_CODE]);

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    /** Lista campo a campo de um 422 — renderizável como bullets. */
    public readonly errors: FieldError[] = [],
    /** O id pra citar no suporte — liga o erro ao log do servidor. */
    public readonly requestId: string | null = null,
    /** Quantos segundos esperar antes de tentar de novo. Só vem num
     * 429, do header `Retry-After`. É o que permite pausar o polling e
     * mostrar contagem regressiva em vez de deixar a pessoa clicando —
     * cada clique a mais é outro 429, que empurra a liberação. */
    public readonly retryAfterSeconds: number | null = null,
    /** O `code` estruturado do corpo, quando o servidor manda um. A
     * tela decide por ELE e nunca pelo texto de `message`: o texto é
     * pra quem lê e pode mudar de redação sem aviso. */
    public readonly code: string | null = null
  ) {
    super(message);
  }

  /** Mensagem + detalhes por campo + código de suporte, pronta pra um
   * alerta multi-linha (`white-space: pre-line`).
   *
   * **O 429 não leva código de suporte** (decisão da dona, 2026-09-10):
   * ele não é falha, é "você foi rápido demais". O código serve pra
   * alguém achar a linha no log do servidor, e aqui não há o que
   * procurar — mostrá-lo faz um limite esperado parecer defeito, e
   * rende chamado à toa.
   *
   * **Nem o `no_access`** (2026-10-15), pelo mesmo motivo: "você não tem
   * acesso, fale com um administrador" é decisão de alguém, não defeito
   * a procurar no log — com o código embaixo, parecia erro do sistema.
   *
   * **Nem o `prompt_too_large`** (2026-10-29): "encurte o texto" é o que
   * resolve, e o código faria a pessoa abrir chamado em vez de encurtar. */
  displayMessage(): string {
    const lines = [this.message];
    for (const item of this.errors) {
      lines.push(item.field ? `• ${item.field}: ${item.message}` : `• ${item.message}`);
    }
    if (this.requestId && this.status !== 429 && !(this.code && WITHOUT_SUPPORT_CODE.has(this.code))) {
      lines.push(`Código de suporte: ${this.requestId}`);
    }
    return lines.join("\n");
  }
}

/** Lê o `Retry-After` de um 429.
 *
 *  O header só chega ao script por causa do `expose_headers` do CORS
 *  (`web_api/app.py`): o front é cross-origin nos dois ambientes, e sem
 *  aquela linha isto seria sempre `null` mesmo com o servidor mandando.
 *
 *  A RFC permite segundos OU uma data HTTP, e `Number()` numa data dá
 *  `NaN` — as duas formas são tratadas, e o que não vira número vira
 *  `null`. A tela então mostra o erro sem contagem, que é degradar em
 *  vez de quebrar. */
function retryAfterFrom(response: Response): number | null {
  if (response.status !== 429) return null;
  const raw = response.headers.get("Retry-After");
  if (!raw) return null;
  const segundos = Number(raw.trim());
  if (Number.isFinite(segundos)) return Math.max(0, Math.ceil(segundos));
  const quando = Date.parse(raw);
  if (Number.isNaN(quando)) return null;
  return Math.max(0, Math.ceil((quando - Date.now()) / 1000));
}

/** O que mostrar pra quem está na tela, a partir do que o `catch`
 *  pegou. Dono único das duas decisões que estavam espalhadas.
 *
 *  **Erro do servidor mostra tudo o que veio**: a mensagem, a lista
 *  campo a campo de um 422 e o código de suporte. O contrato de erro
 *  (`{message, errors[], request_id}`) sempre carregou os três, e nada
 *  na interface chegava a exibir os dois últimos — um 422 aparecia como
 *  uma frase genérica, e o `request_id`, que liga a falha ao log do
 *  servidor, morria no objeto.
 *
 *  **Qualquer outra falha mostra o texto da tela**, nunca o do
 *  navegador. Metade dos `catch` fazia `instanceof Error` e deixava
 *  passar coisas como "Failed to fetch" e "NetworkError when attempting
 *  to fetch resource", que não dizem nada a quem está usando o sistema
 *  e mudam de texto conforme o navegador. */
export function errorMessage(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.displayMessage() : fallback;
}

type CompanyDisabledListener = () => void;
const companyDisabledListeners = new Set<CompanyDisabledListener>();

/**
 * Avisa quando QUALQUER chamada volta o 403 de empresa desativada.
 *
 * Fica aqui, e não em cada tela, porque a empresa pode ser desativada
 * por um Máster enquanto outra pessoa está com ela aberta — e a
 * primeira chamada a notar é de uma tela qualquer. Quem assina é a
 * casca, que leva a pessoa de volta ao hub com o aviso. Devolve a função
 * que cancela a assinatura.
 */
export function onCompanyDisabled(listener: CompanyDisabledListener): () => void {
  companyDisabledListeners.add(listener);
  return () => {
    companyDisabledListeners.delete(listener);
  };
}

type AccessRevokedListener = (message: string) => void;
const accessRevokedListeners = new Set<AccessRevokedListener>();

/**
 * Avisa quando QUALQUER chamada volta o 403 `no_access` — a conta foi
 * inativada com a pessoa no meio do uso, e a partir dali TODA chamada
 * recusa. Quem assina é a casca, que encerra a sessão local e mostra a
 * mensagem na tela de login, em vez de cada tela exibir o mesmo erro
 * solto. Recebe o texto do servidor. Devolve a função que cancela.
 */
export function onAccessRevoked(listener: AccessRevokedListener): () => void {
  accessRevokedListeners.add(listener);
  return () => {
    accessRevokedListeners.delete(listener);
  };
}

function normalizeErrors(raw: WireError["errors"]): FieldError[] {
  if (!Array.isArray(raw)) return [];
  return raw.map(item => (typeof item === "string" ? { message: item } : item));
}

/** O que dizer quando a resposta não tem a mensagem do NOSSO servidor.
 *
 *  Quem responde assim é quase sempre o balanceador da AWS, com uma
 *  página HTML: 504 quando a chamada passa do minuto que ele espera, 502
 *  e 503 quando a API caiu ou está subindo. Era "Erro HTTP 504" na tela
 *  (relato da dona, 2026-10-27: "o usuário não tem ideia do que é"), e o
 *  número não ajuda ninguém — não há código de suporte, porque a linha
 *  nunca chegou ao nosso log. */
export function unreadableResponseMessage(status: number): string {
  if (status === 504) {
    return "O servidor demorou demais para responder e a conexão foi encerrada. Tente de novo em alguns instantes; se continuar, abra um chamado na Central de ajuda.";
  }
  if (status === 502 || status === 503) {
    return "O sistema está fora do ar por um instante. Tente de novo em alguns segundos.";
  }
  if (status === 413) return "O arquivo enviado é grande demais.";
  return "Não foi possível concluir a ação. Tente de novo; se continuar, abra um chamado na Central de ajuda.";
}

/** Monta o `ApiError` a partir de uma resposta não-2xx. Nunca lança por
 * corpo ilegível — erro de rede/HTML no lugar de JSON vira a frase de
 * `unreadableResponseMessage`, sem o número cru. */
export async function toApiError(response: Response): Promise<ApiError> {
  const body = (await response.json().catch(() => null)) as WireError | null;
  const message =
    body?.message ??
    // Transição: contrato morto (e FastAPI cru) manda `detail` string.
    (typeof body?.detail === "string" ? body.detail : null) ??
    unreadableResponseMessage(response.status);
  const error = new ApiError(
    response.status,
    message,
    normalizeErrors(body?.errors),
    body?.request_id ?? null,
    retryAfterFrom(response),
    body?.code ?? null
  );
  if (response.status === 403 && error.code === COMPANY_DISABLED_CODE) {
    companyDisabledListeners.forEach(listener => listener());
  }
  if (response.status === 403 && error.code === NO_ACCESS_CODE) {
    accessRevokedListeners.forEach(listener => listener(error.message));
  }
  // 429 fica de fora pelo mesmo motivo do `displayMessage`: é a cerca
  // respondendo, não uma falha que o suporte precise procurar no log.
  if (error.requestId && response.status !== 429 && error.code !== NO_ACCESS_CODE) {
    lastCode = { code: error.requestId, at: Date.now() };
  }
  return error;
}

/** O código de suporte do último erro que a tela recebeu, e quando.
 *
 * A Central de ajuda o oferece pro chamado: quem acabou de ver "Código de
 * suporte: …" num erro não precisa copiar e colar. Só em memória: some ao
 * recarregar a página, que é também quando ele deixa de ser "o erro que
 * acabou de acontecer". */
export type SupportCode = { code: string; at: number };

let lastCode: SupportCode | null = null;

export function lastSupportCode(): SupportCode | null {
  return lastCode;
}

/** Só para testes. */
export function resetLastSupportCode(): void {
  lastCode = null;
}

/**
 * Modo dev: com `VITE_DEV_USER` no `.env.local`, o client fala o stub de
 * autenticação do web_api (`X-User-Id`/`X-Company-Id`) em vez do Bearer
 * do contrato morto — é o que permite clicar no site contra o backend
 * real antes do Keycloak existir. A empresa ativa vem da URL (o
 * AppShellProvider chama `setActiveCompanyId` quando ela muda), então o
 * invariante continua: o servidor VALIDA o vínculo do seletor; o modo
 * dev só troca o formato da credencial, não o modelo de escopo.
 *
 * Morre junto com o stub quando o Keycloak entrar.
 */
const DEV_USER = (import.meta.env.VITE_DEV_USER as string | undefined) || null;

/**
 * **A precedência tem UM dono, e é esta linha.**
 *
 * Keycloak configurado desliga o modo dev, espelhando a regra do
 * servidor (`web_api/infrastructure/http/security.py`: havendo
 * validador de token, `X-User-Id` não vale nada em ambiente nenhum).
 *
 * Escrito aqui, e não em cada consumidor, porque a primeira versão
 * disto tinha a decisão em DOIS lugares com ordens opostas — a casca
 * checava o modo dev primeiro e entrava nele, enquanto o `applyAuth`
 * checava o Keycloak primeiro e mandava `Bearer luiza`. Resultado: 401
 * em tudo, num ambiente onde as duas variáveis estavam definidas, com a
 * tela dizendo que o backend tinha caído.
 */
export const devAuthEnabled = DEV_USER !== null && !oidcEnabled;
export const devUserId = DEV_USER;

let activeCompanyId: string | null = null;

export function setActiveCompanyId(companyId: string | null): void {
  activeCompanyId = companyId;
}

/**
 * Empresa da chamada, quando ela não é a da URL.
 *
 * A Home (`/`) não tem `:companyId` na rota, então `activeCompanyId` é
 * `null` ali — e o resumo dela é sobre a última empresa visitada. Em vez
 * de mexer no singleton (que é derivado da URL e voltaria a divergir no
 * próximo `useEffect` do shell), a chamada carrega a empresa dela.
 */
export type RequestScope = { companyId?: string };

/**
 * Acrescenta os headers de autenticação a um `Headers` já montado.
 *
 * Exportada porque nem toda chamada passa pelo `request()` acima: o
 * stream do Copiloto usa `fetch` direto (SSE não cabe no caminho JSON) e
 * precisa da MESMA autenticação. Enquanto isso era um objeto literal de
 * headers escrito à mão, as rotas de fora do `request()` mandavam só o
 * `Authorization` e tomavam 401 em desenvolvimento, onde a identidade é
 * o `X-User-Id`. (A transcrição de áudio, que também passava por aqui,
 * saiu em 2026-10-09 junto com o ditado por voz.)
 */
export function applyAuth(
  headers: Headers,
  token?: string,
  scope?: RequestScope
): void {
  /**
   * O escopo por empresa vale nos DOIS modos, e o `X-Company-Id` fica
   * FORA do ramo de dev de propósito: o back exige o header em toda rota
   * autenticada, hoje e depois do Keycloak (que muda só a identidade —
   * ver `web_api/infrastructure/http/security.py`). Enquanto ele vivia
   * só no ramo de dev, qualquer build sem `VITE_DEV_USER` tomava 401 em
   * tudo.
   */
  const companyId = scope?.companyId ?? activeCompanyId;
  if (companyId) headers.set("X-Company-Id", companyId);
  /**
   * **Keycloak ganha do stub, e a ordem espelha a do servidor.**
   * `web_api/infrastructure/http/security.py` recusa `X-User-Id` assim
   * que há validador de token — em QUALQUER ambiente. Se aqui a ordem
   * fosse a inversa, um `.env.local` com as duas coisas mandaria o
   * header e tomaria 401 em tudo, com a tela dizendo que o backend caiu.
   */
  if (devAuthEnabled) {
    headers.set("X-User-Id", DEV_USER as string);
    return;
  }
  /**
   * `||`, e não `??`, e a diferença tem custo medido.
   *
   * `??` só cai no fallback com `null`/`undefined`, então um token
   * VAZIO (`""`) passava como se fosse um token — a chamada saía sem
   * header nenhum e tomava 401. Funcionava mesmo assim, pelo caminho
   * reativo logo abaixo, e era esse o problema: cada login fazia um
   * 401 inútil e gastava uma rotação de refresh token pra se
   * recuperar. Apareceu no log do servidor como `/api/me` 401 seguido
   * de `/api/me` 200 do mesmo cliente, um par por login.
   *
   * Não existe caso em que `""` signifique "quero chamar sem
   * autenticação": ou há sessão, ou a rota é pública e ninguém passa
   * por aqui.
   *
   * **A sessão ganha do token recebido** (2026-11-04). O shell não troca
   * mais o `token` do contexto a cada renovação (~5 min): trocar fazia
   * toda tela com o token nas dependências buscar tudo de novo, e o
   * Versa recarregava o relatório inteiro no meio do trabalho. O token
   * que as telas passam é o do login, só um sinal de "há sessão"; o que
   * vai no header é o mais novo, lido aqui na hora da chamada.
   */
  const bearer = currentAccessToken() || token;
  if (bearer) headers.set("Authorization", `Bearer ${bearer}`);
}

/**
 * Uma tentativa de renovar e repetir, quando o servidor devolve 401.
 *
 * É a metade REATIVA da renovação (a proativa é o temporizador de
 * `session.ts`), e existe pro caso que o temporizador não alcança: aba
 * adormecida, máquina suspensa, relógio que andou. Sem ela, voltar do
 * almoço e clicar em qualquer coisa dá erro uma vez.
 *
 * **Repete no máximo UMA vez, e só quando o token mudou.** Um 401 que
 * não é de token vencido — "informe a empresa", vínculo removido — volta
 * com o mesmo token na mão, e repetir viraria laço.
 */
/** O token que o `applyAuth` pôs no header — o que o 401 recusou. */
function sentBearer(token?: string): string | null {
  return currentAccessToken() || token || null;
}

async function retryOnce(
  url: string,
  init: RequestInit,
  scope: RequestScope | undefined,
  previous: string | null
): Promise<Response | null> {
  if (!oidcEnabled) return null;
  const renewed = await renew();
  const bearer = renewed?.accessToken ?? null;
  if (!bearer || bearer === previous) return null;
  const headers = new Headers(init.headers);
  applyAuth(headers, bearer, scope);
  return fetch(url, { ...init, headers });
}

export async function request<T>(
  baseUrl: string,
  path: string,
  init: RequestInit = {},
  token?: string,
  scope?: RequestScope
): Promise<T> {
  const headers = new Headers(init.headers);
  applyAuth(headers, token, scope);
  if (init.body && !(init.body instanceof FormData)) {
    headers.set("Content-Type", "application/json");
  }

  const url = `${baseUrl}${path}`;
  let response = await fetch(url, { ...init, headers });
  if (response.status === 401) {
    response = (await retryOnce(url, init, scope, sentBearer(token))) ?? response;
  }
  if (!response.ok) throw await toApiError(response);
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

/** Download binário (planilha reconstruída etc.) com a mesma autenticação. */
export async function requestBlob(
  baseUrl: string,
  path: string,
  token?: string,
  scope?: RequestScope
): Promise<Blob> {
  const headers = new Headers();
  applyAuth(headers, token, scope);
  const url = `${baseUrl}${path}`;
  let response = await fetch(url, { headers });
  if (response.status === 401) {
    response = (await retryOnce(url, {}, scope, sentBearer(token))) ?? response;
  }
  if (!response.ok) throw await toApiError(response);
  return response.blob();
}
